import { useEffect, useState } from "react";
import { PhoneOff, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";

/**
 * Admin → Shop Owners: whether the vendor dashboard shows customers' mobile numbers (iter169).
 * Backward compatible: a tenant that existed before this setting has nothing stored and stays ON (numbers and Call
 * buttons shown, as always); only an explicit OFF hides them. New tenants are created with it OFF. The server
 * enforces it (backend/merchant_privacy.py): with it off, shop owners get no customer number on orders, bookings,
 * printed bills or in-store coupons. Riders and admins are not affected.
 */
export default function CustomerMobileVisibility() {
    const [on, setOn] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get("/admin/tenant-settings")
            // Same rule as the server: OFF only when explicitly false (a missing value is the legacy "shown").
            .then(({ data }) => { if (alive) { setOn(data?.customer_mobile_visible_to_merchant !== false); setLoaded(true); } })
            .catch(() => {});      // not loaded → the switch stays disabled rather than guessing a state
        return () => { alive = false; };
    }, []);

    const toggle = async () => {
        const next = !on;
        setBusy(true);
        setOn(next);
        try {
            await api.patch("/admin/tenant-settings", { customer_mobile_visible_to_merchant: next });
            toast.success(next ? "Shop owners can now see customers' mobile numbers" : "Customers' mobile numbers are now hidden from shop owners");
        } catch (e) {
            setOn(!next);
            toast.error(e?.response?.data?.detail || "Could not save the setting");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="customer-mobile-visibility" className="mt-4 bg-card border border-border rounded-2xl p-4 flex items-start gap-3">
            <div className={`shrink-0 w-9 h-9 rounded-xl grid place-items-center ${loaded && on ? "bg-emerald-50 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                {on ? <Phone size={16} /> : <PhoneOff size={16} />}
            </div>
            <div className="min-w-0 flex-1">
                <p id="cmv-label" className="font-semibold text-sm">Show customers&apos; mobile numbers to shop owners</p>
                <p className="text-xs text-muted-foreground mt-1">
                    {!loaded
                        ? "Loading this setting…"
                        : on
                            ? "Shop owners see the customer's number and a Call button on orders and bookings."
                            : "Hidden: shop owners see the customer's name and delivery address, but not their number (no Call button) — on orders, bookings, printed bills and in-store coupons. Riders and admins always see it."}
                </p>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-labelledby="cmv-label"
                data-testid="customer-mobile-visibility-toggle"
                disabled={!loaded || busy}
                onClick={toggle}
                className={`shrink-0 relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-60 ${on ? "bg-emerald-500" : "bg-muted-foreground/30"}`}
            >
                {busy
                    ? <Loader2 size={14} className="absolute left-1/2 -translate-x-1/2 animate-spin text-white" />
                    : <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />}
            </button>
        </div>
    );
}
